// apps/roleforge-app/src/pages/RespondShell.tsx
//
// Phase 10 Task 10.4 — chat-based stakeholder shell. Replaces the form-based
// RespondPage. The flow:
//
//   1. The hook exchanges the URL token for a Supabase session cookie.
//   2. We hydrate the thread's existing messages + audit/owner display info.
//   3. The SSE-driven chat runs against the same /converse pipeline as
//      the owner's chat, but pointed at THIS thread (cookie auth + thread_id
//      in the body).
//   4. Until the first message lands, an opaque welcome card overlays the
//      transcript so the stakeholder lands in something framed and friendly.

import { useEffect, useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useRespondSession } from '../lib/use-respond-session';
import {
  getThreadMessages,
  getInvitationMeta,
  completeOwnThread,
  type Message,
} from '../lib/api-client';
import { RespondSidebar } from '../components/respond/RespondSidebar';
import { RespondWelcomeCard } from '../components/respond/RespondWelcomeCard';
import {
  RespondConverseNotice,
  respondConverseErrorCopy,
} from '../components/respond/RespondConverseNotice';
import { RespondFinishControl } from '../components/respond/RespondFinishControl';
import { ChatTranscript } from '../components/chat/ChatTranscript';
import { ChatComposer } from '../components/chat/ChatComposer';
import { CreditDisclosureNotice } from '../components/chat/CreditDisclosureNotice';
import { HostedCreditApprovalNotice } from '../components/chat/HostedCreditApprovalNotice';
import type { CreditActionDisclosure } from '@vibecontrol/shared';
import { AssetsColumn } from '../components/audit/AssetsColumn';
import { useOperatingModelState } from '../lib/use-operating-model-state';
import { createSseEventRouter } from '../lib/sse-event-router';
import { streamConverse } from '../lib/converse-stream';
import { parseCustomerCreditDisclosure, SHOW_CUSTOMER_CREDIT_DISCLOSURE } from '../lib/credit-disclosure';
import {
  createRoleforgeActionKey,
  loadPendingRoleforgeApproval,
  isTerminalRoleforgeApprovalEvent,
  pendingRoleforgeApprovalFromEvent,
  savePendingRoleforgeApproval,
  type PendingRoleforgeApproval,
} from '../lib/hosted-credit-approval';

const API_BASE =
  import.meta.env.VITE_VIBECONTROL_API_URL ?? 'https://api.vibecontrol.app';

interface InvitationMeta {
  audit_name: string;
  owner_name: string;
  voice_label: string;
}

interface Props {
  token?: string;
}

export function RespondShell({ token: tokenProp }: Props = {}) {
  const params = useParams<{ token: string }>();
  const token = tokenProp ?? params.token;
  const session = useRespondSession(token);

  // One router per thread mount. Surfaces (operating-model panel, agent
  // activity panel, chat) all subscribe through this.
  const router = useMemo(
    () => createSseEventRouter(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [session.session?.thread_id],
  );
  const operatingModelState = useOperatingModelState(router);

  const [messages, setMessages] = useState<Message[]>([]);
  const [meta, setMeta] = useState<InvitationMeta | null>(null);
  const [welcomeDismissed, setWelcomeDismissed] = useState(false);
  const [streamingText, setStreamingText] = useState('');
  const [sending, setSending] = useState(false);
  const [pendingHostedApproval, setPendingHostedApproval] = useState<PendingRoleforgeApproval | null>(null);
  const [creditDisclosure, setCreditDisclosure] = useState<CreditActionDisclosure | null>(null);
  // Issue #1250: the last non-credit converse error code, so authz refusals
  // (invitation_expired / thread_closed / forbidden) get actionable copy
  // instead of vanishing into a silent no-op.
  const [converseErrorCode, setConverseErrorCode] = useState<string | undefined>(undefined);
  const [finished, setFinished] = useState(false);

  useEffect(() => {
    if (!session.session) return;
    let cancelled = false;
    void Promise.all([
      getThreadMessages(session.session.thread_id),
      getInvitationMeta(session.session.invitation_id),
    ])
      .then(([msgs, m]) => {
        if (cancelled) return;
        setMessages(msgs.messages);
        setMeta(m);
      })
      .catch(() => {
        // Soft-fail — the loading placeholder stays.
      });
    return () => {
      cancelled = true;
    };
  }, [session.session?.thread_id, session.session?.invitation_id]);

  useEffect(() => {
    const current = session.session;
    setPendingHostedApproval(current
      ? loadPendingRoleforgeApproval(`${current.audit_id}:${current.thread_id}`)
      : null);
  }, [session.session?.audit_id, session.session?.thread_id]);

  if (session.state === 'not_found') {
    return (
      <div className="flex h-screen items-center justify-center px-6 text-center text-graphite">
        This invitation can't be found. Ask the person who sent it for a fresh link.
      </div>
    );
  }
  if (session.state === 'revoked') {
    return (
      <div className="flex h-screen items-center justify-center px-6 text-center text-graphite">
        This invitation was withdrawn.
      </div>
    );
  }
  if (session.state === 'error') {
    return (
      <div className="flex h-screen items-center justify-center px-6 text-center text-graphite">
        Something went wrong. Try reopening the link, or ask the person who sent it
        to send a fresh one.
      </div>
    );
  }
  if (session.state === 'loading' || !meta || !session.session) {
    return (
      <div className="flex h-screen items-center justify-center text-graphite">
        Loading…
      </div>
    );
  }

  const showWelcome = !welcomeDismissed && messages.length === 0;

  // Issue #1250: a terminal refusal (expired link, closed thread, no grant)
  // means every further send will be refused too — so retire the composer
  // rather than let the stakeholder type into a dead channel.
  const terminalRefusal =
    converseErrorCode !== undefined && respondConverseErrorCopy(converseErrorCode).terminal;
  const conversationClosed = finished || terminalRefusal;

  const handleFinish = async () => {
    if (!session.session) return;
    await completeOwnThread(session.session.thread_id);
    setFinished(true);
    setConverseErrorCode(undefined);
  };

  const handleSend = async (text: string, continuation?: PendingRoleforgeApproval) => {
    if (!session.session || sending) return;
    setSending(true);
    setCreditDisclosure(null);
    setConverseErrorCode(undefined);
    const scopeKey = `${session.session.audit_id}:${session.session.thread_id}`;
    const actionKey = continuation?.actionKey ?? createRoleforgeActionKey();

    const optimistic: Message = {
      id: `optimistic-${Date.now()}`,
      audit_id: session.session.audit_id,
      role: 'user',
      content: text,
      tool_call_id: null,
      tool_name: null,
      metadata: {},
      position: messages.length,
      created_at: new Date().toISOString(),
    };
    if (!continuation) setMessages((prev) => [...prev, optimistic]);

    let accumulated = '';
    let sawError = false;
    let streamCompleted = false;
    try {
      // 'cookie' sentinel: streamConverse switches to credentials-include
      // mode and the server's authMiddleware reads sb-stakeholder-session.
      for await (const ev of streamConverse(
        API_BASE,
        'cookie',
        session.session.audit_id,
        text,
        {
          thread_id: session.session.thread_id,
          actionKey,
          ...(continuation ? { resumeToken: continuation.resumeToken } : {}),
        },
      )) {
        router.dispatch(ev);
        if (ev.type === 'message_delta') {
          accumulated += ev.text;
          setStreamingText(accumulated);
        } else if (ev.type === 'credit_disclosure') {
          const disclosure = parseCustomerCreditDisclosure(ev.disclosure);
          if (disclosure) setCreditDisclosure(disclosure);
        } else if (ev.type === 'error') {
          sawError = true;
          // Credit-approval codes own their own affordance
          // (HostedCreditApprovalNotice); everything else — notably the #1202
          // authz refusals — routes to the plain-language notice.
          if (typeof ev.code === 'string' && !ev.code.startsWith('credit_spend_')) {
            setConverseErrorCode(ev.code);
          }
          const pending = pendingRoleforgeApprovalFromEvent(text, ev);
          const nextPending = pending
            ?? (continuation && !isTerminalRoleforgeApprovalEvent(ev) ? continuation : null);
          setPendingHostedApproval(nextPending);
          savePendingRoleforgeApproval(scopeKey, nextPending);
        }
      }
      streamCompleted = true;
    } finally {
      if (continuation && streamCompleted && !sawError) {
        setPendingHostedApproval(null);
        savePendingRoleforgeApproval(scopeKey, null);
      }
      // Refresh canonical transcript from the server.
      try {
        const refreshed = await getThreadMessages(session.session.thread_id);
        setMessages(refreshed.messages);
      } catch {
        // keep optimistic state on fetch failure
      }
      setStreamingText('');
      setSending(false);
    }
  };

  return (
    // Issues #1201 / #1235 (same defect on the stakeholder surface): the
    // sidebar and the assets column were unshrinkable, so on a phone the
    // conversation — the only thing a stakeholder is here to do — was
    // squeezed to nothing. Below `lg` the chat gets the whole viewport.
    <div className="fixed inset-0 flex overflow-x-hidden bg-paper text-ink">
      <div className="hidden shrink-0 lg:block" data-testid="respond-sidebar-wrapper">
        <RespondSidebar
          voiceLabel={meta.voice_label}
          sections={[
            { label: 'Your role', state: 'active' },
            { label: 'Your work', state: 'pending' },
            { label: 'Where you get stuck', state: 'pending' },
            { label: "What 'good' looks like", state: 'pending' },
          ]}
        />
      </div>
      <main className="relative flex min-w-0 flex-1 flex-col">
        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-4">
          <ChatTranscript
            messages={messages.filter((m) => m.role !== 'system')}
            streamingAgentText={streamingText || undefined}
            auditId={session.session.audit_id}
          />
        </div>
        <div className="border-t border-line bg-paper px-6 py-3">
          {SHOW_CUSTOMER_CREDIT_DISCLOSURE && creditDisclosure && (
            <CreditDisclosureNotice disclosure={creditDisclosure} className="mb-3" />
          )}
          {pendingHostedApproval && (
            <HostedCreditApprovalNotice
              pending={pendingHostedApproval}
              busy={sending}
              onRetry={() => void handleSend(pendingHostedApproval.message, pendingHostedApproval)}
              onDiscard={() => {
                setPendingHostedApproval(null);
                savePendingRoleforgeApproval(
                  `${session.session!.audit_id}:${session.session!.thread_id}`,
                  null,
                );
              }}
              className="mb-3"
            />
          )}
          {converseErrorCode && (
            <RespondConverseNotice code={converseErrorCode} className="mb-3" />
          )}
          {!conversationClosed && (
            <ChatComposer
              onSend={handleSend}
              disabled={sending}
              placeholder="Tell me about your work…"
            />
          )}
          {/* Hidden once a refusal already closed the conversation — offering
              "I'm finished" on an expired link would just produce a 403. */}
          {!terminalRefusal && (
            <RespondFinishControl
              onConfirm={handleFinish}
              finished={finished}
              disabled={sending}
              className="mt-3"
            />
          )}
        </div>
        {showWelcome && (
          <RespondWelcomeCard
            ownerName={meta.owner_name}
            auditWorkspaceName={meta.audit_name}
            onStart={() => setWelcomeDismissed(true)}
          />
        )}
      </main>
      <div className="hidden shrink-0 lg:block lg:w-80" data-testid="respond-assets-wrapper">
        <AssetsColumn
          state={operatingModelState}
          activeAssetId={null}
          onSelectAsset={() => {}}
        />
      </div>
    </div>
  );
}
